"use client";
/**
 * UnifiedItemSearch.jsx
 *
 * Single item-search modal that replaces both:
 *   • MaterialItemSearch  (TimeAndMaterialTab3)
 *   • ItemSearchHelper3   (MaterialsTab3 / RequestForm)
 *
 * Scoring engine merges:
 *   • T&M steel-framing engine  — MIL→gauge, WEBCODE depth tables, leg/flange
 *   • ItemSearchHelper3 engine  — compound types, dimensional phrases, aliases
 *   • AI intent (parse-search-intent) — for 3-4 digit codes, mil specs, flanges
 *
 * Props
 * ─────
 *   token          string   JWT
 *   projectId      string?  loads T&M project-budget items when provided
 *   budgetItems    array?   pre-loaded budget items (PO context, from parent)
 *   onSelect       fn(item, qty)
 *                    item has all original fields + _source:'budget'|'catalog',
 *                    _isBudget:bool, quantity:number, isCoverage:bool
 *   onClose        fn()
 *   title          string   (default "Add Item")
 *   closeOnSelect  bool     close after each add — true for T&M, false for MO
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";
const cls = (...c) => c.filter(Boolean).join(" ");
const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

// ═══════════════════════════════════════════════════════════════════════════════
// ── NORMALIZATION ─────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const QUOTE_RE = /['"\u2018\u2019\u201c\u201d\u2032\u2033`]/g;
const stripQ = (s) => String(s == null ? "" : s).replace(QUOTE_RE, "");
const FRAC_RE = /\d+[-–]\d+\/\d+|\d+\/\d+/g;
const normFrac = (s) =>
  (s || "").replace(FRAC_RE, (m) => m.replace(/[\s\-\/]/g, "_"));

function norm(s) {
  return normFrac(stripQ(s || ""))
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tok(s) {
  return norm(s)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── STEEL-FRAMING LOOKUP TABLES (T&M engine) ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const MIL2GA = { 18: "25", 33: "20", 43: "18", 54: "16", 68: "14", 97: "12" };

// 3-4 digit shorthand → actual depth in inches (e.g. 158 → 1.625")
const WIDTH_SH = {
  158: 1.625,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  312: 3.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  512: 5.5,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

// Web/flange codes from item-code format (e.g. 350S162-43 → 3.5" stud, 1.625 flange, 18ga)
const WEBCODE = {
  125: 1.25,
  137: 1.375,
  150: 1.5,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

const TRACK_FAM = new Set(["track", "slotted track", "flex track", "j runner"]);

// ═══════════════════════════════════════════════════════════════════════════════
// ── TYPE / GAUGE / LEG DETECTION ──────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const TYPE_RULES = [
  [
    "insulation",
    [
      /insulation/,
      /comfortbatt/,
      /rockwool/,
      /roxul/,
      /mineral wool/,
      /safe.?n.?sound/,
      /\bbatt\b/,
      /fiberglass/,
    ],
  ],
  ["slotted track", [/slotted track/]],
  ["flex track", [/flex track/]],
  ["j runner", [/j-?\s*runner/]],
  ["ch stud", [/\bch stud/]],
  ["carrying channel", [/carrying channel/]],
  ["furring channel", [/furring/]],
  ["resilient channel", [/resilient/]],
  ["z bar", [/\bz ?bar/]],
  ["hanger wire", [/hanger wire/]],
  ["tie wire", [/tie wire/]],
  ["angle", [/\bangle\b/, /d700/, /\bl[ -]track\b/]],
  ["track", [/\btracks?\b/]],
  ["stud", [/\bstuds?\b/]],
  ["screw", [/screw/]],
  ["drywall", [/drywall/, /gypsum/, /\bdensglass\b/, /sheathing/]],
];

// Compound multi-word product identifiers (ItemSearchHelper3 engine)
const COMPOUNDS = [
  ["z", "bar"],
  ["z", "furring"],
  ["hat", "channel"],
  ["resilient", "channel"],
  ["rc", "channel"],
  ["corner", "bead"],
  ["l", "bead"],
  ["j", "bead"],
  ["j", "trim"],
  ["cee", "stud"],
  ["drywall", "screw"],
  ["type", "s"],
  ["type", "x"],
  ["tek", "screw"],
  ["lay", "in"],
  ["drop", "ceiling"],
];

// Token aliases (ItemSearchHelper3 engine)
const ALIASES = {
  dw: ["drywall"],
  gb: ["gypsum"],
  gyp: ["gypsum"],
  std: ["stud"],
  mtl: ["metal"],
  cgc: ["cgc"],
  ussg: ["usg"],
  "25ga": ["25 ga", "25gauge"],
  "20ga": ["20 ga"],
  "18ga": ["18 ga"],
  "16ga": ["16 ga"],
  "14ga": ["14 ga"],
};

// Dimensional phrase patterns (ItemSearchHelper3 engine)
const DIM_DESCS =
  "leg|stud|track|flange|web|wing|return|lip|face|bar|channel|bead";
const DIM_PATS = [
  new RegExp(
    `(\\d+(?:[-–]\\d+\\/\\d+)?(?:\\/\\d+)?)["']?\\s*(${DIM_DESCS})`,
    "gi",
  ),
  /(\d+)\s*(ga|gauge|mil)\b/gi,
  /(\d+\/\d+)["']/gi,
];

function toVal(s) {
  const t = stripQ(String(s == null ? "" : s))
    .trim()
    .toLowerCase();
  let m = t.match(/^(\d+)[-\s](\d+)\/(\d+)$/);
  if (m) return +m[1] + +m[2] / +m[3];
  m = t.match(/^(\d+)\/(\d+)$/);
  if (m) return +m[1] / +m[2];
  m = t.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

const approx = (a, b, tol = 0.06) =>
  a != null && b != null && Math.abs(a - b) <= tol;

function detectType(text) {
  const t = norm(text);
  for (const [name, pats] of TYPE_RULES)
    if (pats.some((p) => p.test(t))) return name;
  return null;
}

function detectGauge(text) {
  const t = norm(text);
  let m = t.match(/(\d{1,2})\s*ga(?:uge)?\b/);
  if (m) return String(parseInt(m[1], 10));
  m = t.match(/(\d{2})\s*mil\b/);
  if (m) return MIL2GA[m[1]] || null;
  return null;
}

function detectLeg(text) {
  const m = norm(text).match(
    /(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/,
  );
  return m ? toVal(m[1]) : null;
}

// Parse a 350S162-43-style item code into structured spec
function getItemSpec(item) {
  const code = stripQ(item.itemCode || "").toUpperCase();
  const desc =
    item.description || item.costCodeDescription || item.itemDescription || "";
  const spec = { depth: null, type: null, gauge: null, leg: null };
  const m = code.match(/^(\d{3,4})([ST])(\d{2,3})-(\d{2,3})/);
  if (m) {
    spec.depth = WEBCODE[parseInt(m[1], 10)] ?? null;
    spec.type = m[2] === "S" ? "stud" : "track";
    spec.leg = WEBCODE[parseInt(m[3], 10)] ?? null;
    spec.gauge = MIL2GA[parseInt(m[4], 10)] || null;
  }
  const dtype = detectType(desc);
  if (dtype) spec.type = dtype;
  if (spec.depth == null) {
    const md = desc
      .toLowerCase()
      .match(/^\s*(\d+(?:-\d+\/\d+)?)\s*["\u201d\u2033]/);
    if (md) spec.depth = toVal(md[1]);
  }
  if (spec.gauge == null) spec.gauge = detectGauge(desc);
  if (spec.leg == null) spec.leg = detectLeg(desc);
  return spec;
}

function getQuerySpec(q) {
  const spec = {
    depth: null,
    type: detectType(q),
    gauge: detectGauge(q),
    leg: detectLeg(q),
  };
  // Strip already-parsed dimensional tokens before looking for depth
  const stripped = norm(q)
    .replace(/(\d{1,2})\s*ga(?:uge)?\b/g, " ")
    .replace(/(\d{2})\s*mil\b/g, " ")
    .replace(/(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/g, " ");
  const tokens = tok(stripped);
  let digits = "";
  let i = 0;
  while (i < tokens.length && /^\d+$/.test(tokens[i])) {
    digits += tokens[i];
    i++;
  }
  if (digits) {
    spec.depth =
      WIDTH_SH[parseInt(digits, 10)] != null
        ? WIDTH_SH[parseInt(digits, 10)]
        : toVal(tokens[0]);
  }
  return spec;
}

function getCompounds(str) {
  const tokens = tok(str);
  const found = new Set();
  for (const [a, b] of COMPOUNDS) {
    for (let i = 0; i < tokens.length - 1; i++) {
      if (tokens[i] === a && tokens[i + 1] === b) found.add(`${a} ${b}`);
    }
    if (tokens.includes(`${a}${b}`)) found.add(`${a} ${b}`);
  }
  return found;
}

function getDimPhrases(str) {
  const n = norm(str);
  const phrases = new Set();
  for (const re of DIM_PATS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(n)) !== null) {
      const num = normFrac(m[1])
        .replace(/[\s\-\/]/g, "_")
        .toLowerCase();
      const desc = (m[2] || "").toLowerCase();
      const d = desc === "gauge" ? "ga" : desc === "mil" ? "mil" : desc;
      phrases.add(d ? `${num} ${d}` : num);
    }
  }
  return phrases;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── UNIFIED SCORE ─────────────────────────────────────────────────────────────
// Handles: steel studs/track (depth/gauge/leg), drywall, screws, insulation,
//          compound types (z bar, resilient channel), SF/LF items, general text
// ═══════════════════════════════════════════════════════════════════════════════

function scoreItem(item, rawQuery, intent = null) {
  const qToks = tok(rawQuery);
  if (!qToks.length) return 0;
  const uniqueQ = [...new Set(qToks)];

  const qs = getQuerySpec(rawQuery);
  const isFraming = !!(qs.type || qs.gauge || qs.leg != null);
  let score = 0;

  // ── 1. Structured steel-framing (T&M engine) ────────────────────────────
  if (isFraming) {
    const isp = getItemSpec(item);
    if (qs.type && isp.type) {
      if (qs.type === isp.type) score += 200;
      else if (TRACK_FAM.has(qs.type) && TRACK_FAM.has(isp.type)) score += 70;
      else score -= 220;
    } else if (qs.type && !isp.type) score -= 40;

    if (qs.depth != null && isp.depth != null)
      score += approx(qs.depth, isp.depth) ? 150 : -160;
    if (qs.gauge && isp.gauge) score += qs.gauge === isp.gauge ? 100 : -110;
    if (qs.leg != null && isp.leg != null)
      score += approx(qs.leg, isp.leg) ? 80 : -45;

    // Full-spec match bonus
    const specKeys = ["type", "depth", "gauge"].filter((k) => qs[k] != null);
    if (
      specKeys.length &&
      specKeys.every(
        (k) =>
          isp[k] != null &&
          (k === "depth" ? approx(qs[k], isp[k]) : qs[k] === isp[k]),
      )
    )
      score += 60;
  }

  // ── 2. AI intent scoring (ItemSearchHelper3 engine) ─────────────────────
  if (intent) {
    const allText = norm(
      [
        item.itemCode,
        item.description,
        item.costCodeDescription,
        item.itemDescription,
        item.size,
        item.uom,
      ]
        .filter(Boolean)
        .join(" "),
    );
    const allToks = new Set(tok(allText));
    const reliable =
      intent.productType ||
      intent.gauge ||
      (intent.webSize && /[-\/]/.test(intent.webSize));

    if (reliable) {
      if (intent.webSize) {
        const ns = norm(intent.webSize);
        const hit =
          allText.includes(ns) ||
          tok(intent.webSize).every((t) => allToks.has(t));
        if (hit) score += 120;
        else if (/\d[_\-\/]\d/.test(allText)) score -= 80;
      }
      if (intent.productType) {
        const TYPE_KW = {
          stud: ["stud", "studs"],
          track: ["track", "tracks"],
          "slotted track": ["slotted", "sdt"],
          "z bar": ["z-bar", "z bar"],
          "furring channel": ["furring", "fch"],
          "resilient channel": ["resilient", "rch"],
          angle: ["angle"],
          screw: ["screw"],
          insulation: ["insulation", "rockwool", "comfortbatt"],
          drywall: ["drywall", "gypsum", "gyp"],
        };
        const kws = TYPE_KW[intent.productType] || [intent.productType];
        if (kws.some((kw) => allText.includes(norm(kw)))) score += 100;
        else score -= 70;
      }
      if (intent.gauge) {
        const GA_SIG = {
          25: ["-18", "25ga"],
          20: ["-33", "20ga"],
          18: ["-43", "18ga"],
          16: ["-54", "16ga"],
          14: ["-68", "14ga"],
          12: ["-97", "12ga"],
        };
        const sigs = GA_SIG[intent.gauge] || [`${intent.gauge}ga`];
        if (sigs.some((s) => allText.includes(norm(s)))) score += 100;
        else if (/\d{2}ga/.test(allText)) score -= 60;
      }
    }
  }

  // ── 3. Compound product types (ItemSearchHelper3 engine) ────────────────
  const itemDesc =
    item.description || item.costCodeDescription || item.itemDescription || "";
  const qComps = getCompounds(rawQuery);
  const iComps = getCompounds(itemDesc);
  const compToks = new Set();
  for (const qc of qComps) {
    if (iComps.has(qc)) {
      score += 90;
      qc.split(" ").forEach((t) => compToks.add(t));
    } else score -= 40;
  }

  // ── 4. Dimensional phrase matching (ItemSearchHelper3 engine) ───────────
  const qDim = getDimPhrases(rawQuery);
  const iDim = getDimPhrases(itemDesc);
  const qDescMap = new Map();
  let dimScore = 0;
  for (const qp of qDim) {
    const [val, desc] = qp.includes(" ") ? qp.split(" ") : [qp, ""];
    if (desc) qDescMap.set(desc, val);
    if (iDim.has(qp)) dimScore += 80;
  }
  for (const [desc, val] of qDescMap) {
    const hasSame = [...iDim].some((p) => p.endsWith(" " + desc));
    if (hasSame && !iDim.has(`${val} ${desc}`)) dimScore -= 60;
  }
  score += dimScore;

  // ── 5. Item-code exact / prefix ─────────────────────────────────────────
  const cn = norm(item.itemCode);
  const qn = norm(rawQuery);
  if (cn && qn === cn) score += 150;
  else if (cn && qn.length >= 3 && cn.startsWith(qn)) score += 80;

  // ── 6. Token-level fuzzy scoring ────────────────────────────────────────
  const hayAll = norm(
    [
      item.itemCode,
      item.description,
      item.costCodeDescription,
      item.itemDescription,
      item.size,
      item.uom,
      item.sectionNumber,
    ]
      .filter(Boolean)
      .join(" "),
  );
  const haySet = new Set(tok(hayAll));
  const descToks = tok(norm(itemDesc));
  const matched = [];
  const unmatched = [];

  for (const qt of uniqueQ) {
    if (compToks.has(qt)) {
      matched.push(qt);
      continue;
    }
    if (/^\d{1,2}ga$/.test(qt)) continue;
    let hit = 0;
    const shortNum = /^\d{1,2}$/.test(qt);
    if (norm(item.itemCode) === qt) hit = 100;
    else if (
      !shortNum &&
      qt.length >= 3 &&
      norm(item.itemCode || "").startsWith(qt)
    )
      hit = 60;
    else if (descToks.includes(qt)) hit = shortNum ? 20 : 50;
    else if (haySet.has(qt)) hit = shortNum ? 12 : 30;
    else {
      let aliasHit = false;
      for (const [canonical, alts] of Object.entries(ALIASES)) {
        const forms = [canonical, ...alts].map(norm);
        if (
          forms.includes(qt) &&
          forms.some((f) => haySet.has(f) || hayAll.includes(f))
        ) {
          hit = 20;
          aliasHit = true;
          break;
        }
      }
      if (!aliasHit && !shortNum && qt.length >= 3 && hayAll.includes(qt))
        hit = 5;
    }
    if (hit > 0) {
      score += hit;
      matched.push(qt);
    } else unmatched.push(qt);
  }

  // Unmatched penalties — soften when AI already matched structural fields
  for (const ut of unmatched) {
    const isNum = /^\d+$/.test(ut);
    const isFrac = /_/.test(ut) || /\d+[-\/]\d/.test(ut);
    const base = isNum ? 10 : isFrac ? 25 : 25;
    score -= intent ? base * 0.4 : base;
  }

  // Completion bonuses
  if (matched.length === uniqueQ.length) score += 30;
  const contentToks = uniqueQ.filter((t) => !/^\d+$/.test(t));
  if (
    contentToks.length > 0 &&
    contentToks.every((t) => descToks.includes(t) || compToks.has(t))
  )
    score += 40;

  return score;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── AI INTENT ─────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const AI_TRIGGERS = [
  /(?<![a-zA-Z])\b(158|162|212|250|312|358|362|400|412|600|800|1000|1200)\b(?!\s*['"])/,
  /\b(\d+\/\d+|\d{3,})\s*(flange|leg|\bfl\b)/i,
  /\b\d+\s*mil\b/i,
];
const intentCache = new Map();

function needsAI(q) {
  return AI_TRIGGERS.some((re) => re.test(q));
}

async function fetchIntent(q, token) {
  const key = q.trim().toLowerCase();
  if (intentCache.has(key)) return intentCache.get(key);
  if (key.length < 3 || !needsAI(q)) return null;
  try {
    const res = await fetch(`${API}/aiSearch/parse-search-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: q.slice(0, 150) }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;
    intentCache.set(key, data.intent);
    return data.intent;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── COVERAGE PARSER ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function smartQuotes(s) {
  return (s || "")
    .replace(/[\u2018\u2019\u2032\u02BC`]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"');
}

function feetFromStr(s) {
  s = smartQuotes(s || "").trim();
  if (!s) return null;
  let m = s.match(/^(\d+(?:\.\d+)?)'[\s-]*([\d\-\/]+)\s*"?$/);
  if (m) {
    const in_ = toVal(m[2]);
    return in_ !== null ? parseFloat(m[1]) + in_ / 12 : null;
  }
  m = s.match(/^(\d+(?:\.\d+)?)'$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+(?:\.\d+)?)"$/);
  if (m) return parseFloat(m[1]) / 12;
  const n = toVal(s);
  return n !== null ? n : null;
}

function parseCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = smartQuotes(size).trim();
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = feetFromStr(parts[0]),
        b = feetFromStr(parts[1]);
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = feetFromStr(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── HIGHLIGHT ─────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    const tn = norm(t);
    let idx = 0;
    while ((idx = lower.indexOf(tn, idx)) !== -1) {
      ranges.push([idx, idx + tn.length]);
      idx += tn.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export default function UnifiedItemSearch({
  token,
  projectId = null,
  budgetItems: propBudget = [],
  onSelect,
  onClose,
  title = "Add Item",
  closeOnSelect = false,
}) {
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [projBudget, setProjBudget] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [intent, setIntent] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [added, setAdded] = useState(new Set()); // "itemCode||size" keys
  const [qtys, setQtys] = useState({});
  const [sizes, setSizes] = useState({}); // size overrides for SF/LF
  const inputRef = useRef(null);

  // ── Load catalog + project budget ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const hdrs = { Authorization: `Bearer ${token}` };
    setLoadingCatalog(true);
    if (projectId) setLoadingBudget(true);

    Promise.allSettled([
      axios.get(`${API}/item-database/catalog`, {
        headers: hdrs,
        params: { category: "M", isActive: true },
      }),
      projectId
        ? axios.get(`${API}/tandm3/project/${projectId}/item-search`, {
            headers: hdrs,
            params: { limit: 500 },
          })
        : Promise.resolve({ data: { items: [] } }),
    ]).then(([catR, budR]) => {
      if (cancelled) return;
      if (catR.status === "fulfilled") setCatalog(catR.value.data.items || []);
      if (budR.status === "fulfilled")
        setProjBudget(budR.value.data.items || []);
      setLoadingCatalog(false);
      setLoadingBudget(false);
    });

    return () => {
      cancelled = true;
    };
  }, [token, projectId]);

  // ── AI intent (debounced 420 ms) ──────────────────────────────────────────
  useEffect(() => {
    if (!query.trim() || query.trim().length < 3) {
      setIntent(null);
      return;
    }
    const t = setTimeout(async () => {
      setIntentLoading(true);
      setIntent(await fetchIntent(query, token));
      setIntentLoading(false);
    }, 420);
    return () => clearTimeout(t);
  }, [query, token]);

  // ── Auto-focus ────────────────────────────────────────────────────────────
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  // ── Merge budget + catalog, dedup by itemCode+size ────────────────────────
  // Budget items (from prop or project endpoint) take priority over catalog.
  // Budget items get a +20 score bonus in ranked results.
  const merged = useMemo(() => {
    const byKey = new Map();
    [...propBudget, ...projBudget].forEach((item) => {
      const k = `${(item.itemCode || "").toLowerCase()}||${(item.size || "").toLowerCase()}`;
      if (!byKey.has(k))
        byKey.set(k, { ...item, _source: "budget", _isBudget: true });
    });
    catalog.forEach((item) => {
      const k = `${(item.itemCode || "").toLowerCase()}||${(item.size || "").toLowerCase()}`;
      if (!byKey.has(k))
        byKey.set(k, { ...item, _source: "catalog", _isBudget: false });
    });
    return [...byKey.values()];
  }, [propBudget, projBudget, catalog]);

  const queryToks = useMemo(() => tok(query), [query]);
  const isSearching = query.trim().length > 0;

  // ── Score and sort ────────────────────────────────────────────────────────
  const results = useMemo(() => {
    if (!isSearching) {
      return [...merged].sort((a, b) => {
        if (a._isBudget !== b._isBudget) return a._isBudget ? -1 : 1;
        return (a.itemCode || "").localeCompare(b.itemCode || "");
      });
    }
    return merged
      .map((item) => ({
        item,
        score: scoreItem(item, query, intent) + (item._isBudget ? 20 : 0),
      }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 100)
      .map((r) => r.item);
  }, [merged, query, intent, isSearching]);

  const getKey = (item) =>
    `${(item.itemCode || "").toLowerCase()}||${(item.size || "").toLowerCase()}`;

  const handleAdd = useCallback(
    (item) => {
      const k = getKey(item);
      const uom = item.uom || "";
      const isCov = isCoverageBased(uom);
      const rawSize = sizes[k] ?? item.size ?? "";
      const qty = parseFloat(qtys[k]) || 1;

      onSelect(
        {
          ...item,
          size: rawSize,
          quantity: qty,
          isCoverage: isCov,
        },
        qty,
      );

      setAdded((prev) => new Set(prev).add(k));
      if (closeOnSelect) onClose();
    },
    [onSelect, onClose, closeOnSelect, sizes, qtys],
  );

  const budgetCount = useMemo(
    () => new Set([...propBudget, ...projBudget].map((i) => i.itemCode)).size,
    [propBudget, projBudget],
  );

  const loading = loadingCatalog || loadingBudget;
  const totalAdded = added.size;

  // ── Intent summary pill ───────────────────────────────────────────────────
  const intentSummary = intent
    ? [
        intent.productType,
        intent.webSize,
        intent.gauge && `${intent.gauge}ga`,
        intent.flange,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl mx-4 max-h-[88vh]">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-white">{title}</h3>
            <div className="flex items-center gap-3 mt-0.5">
              {budgetCount > 0 && (
                <span className="text-[10px] font-semibold text-teal-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
                  {budgetCount} budget item{budgetCount !== 1 ? "s" : ""}
                </span>
              )}
              {!loading && (
                <span className="text-[10px] text-gray-500">
                  {merged.length} total · {catalog.length} catalog
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="px-5 py-3 border-b border-gray-700/50 shrink-0">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              🔍
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Code, description, size, gauge… e.g. "3-5/8 stud 25ga", "158S125-18", "drywall 5/8", "resilient channel"'
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-800 border border-gray-600 rounded-xl text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  setIntent(null);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-lg"
              >
                ×
              </button>
            )}
          </div>

          {/* Token + AI status row */}
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              {queryToks.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 text-[9px] font-mono bg-gray-700 text-gray-300 rounded border border-gray-600"
                >
                  {t}
                </span>
              ))}
              {intentLoading && (
                <span className="text-[10px] text-orange-400 animate-pulse font-medium">
                  AI parsing…
                </span>
              )}
              {intentSummary && !intentLoading && (
                <span className="text-[10px] text-orange-300 font-semibold px-2 py-0.5 bg-orange-900/30 border border-orange-800/50 rounded-full">
                  ⚡ {intentSummary}
                </span>
              )}
            </div>
            <span className="text-[10px] text-gray-600 shrink-0">
              {loading
                ? "Loading…"
                : isSearching
                  ? `${results.length} result${results.length !== 1 ? "s" : ""}`
                  : `${merged.length} items`}
            </span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center gap-2.5 py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-700 border-t-orange-500" />
              <span className="text-sm text-gray-400">Loading items…</span>
            </div>
          )}

          {!loading && !isSearching && merged.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-500">
              <span className="text-3xl">📋</span>
              <p className="text-sm">
                No items found. Check catalog is populated.
              </p>
            </div>
          )}

          {!loading && isSearching && results.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-500">
              <span className="text-3xl">🔍</span>
              <p className="text-sm">No results for "{query}"</p>
              <p className="text-xs text-gray-600">
                Try fewer keywords — e.g. "25ga stud" or "5/8 drywall"
              </p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-gray-900 border-b border-gray-700/70">
                {/* Section header when not searching */}
                {!isSearching && budgetCount > 0 && (
                  <tr>
                    <th colSpan={6} className="px-3 py-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
                        <span className="text-[10px] font-semibold text-teal-300 uppercase tracking-wide">
                          Budget items first — type to search catalog
                        </span>
                      </div>
                    </th>
                  </tr>
                )}
                <tr className="border-b border-gray-700/50">
                  <th className="px-3 py-2 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider w-36">
                    Code
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider w-28">
                    Size / Dims
                  </th>
                  <th className="px-3 py-2 text-center text-[10px] text-gray-500 font-medium uppercase tracking-wider w-12">
                    UOM
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider w-20">
                    Section
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-36">
                    Qty / Add
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-800">
                {/* Catalog divider when not searching and there are both types */}
                {(() => {
                  let shownCatalogDivider = false;
                  return results.map((item) => {
                    const k = getKey(item);
                    const uom = item.uom || "";
                    const isCov = isCoverageBased(uom);
                    const rawSize = sizes[k] ?? item.size ?? "";
                    const coverage = isCov ? parseCoverage(rawSize, uom) : null;
                    const covOk = coverage !== null;
                    const isAdded = added.has(k);

                    // Catalog divider (no-search mode only)
                    let divider = null;
                    if (
                      !isSearching &&
                      !item._isBudget &&
                      !shownCatalogDivider &&
                      budgetCount > 0
                    ) {
                      shownCatalogDivider = true;
                      divider = (
                        <tr key={`__div__${k}`} className="bg-gray-900/60">
                          <td colSpan={6} className="px-3 py-1.5">
                            <span className="text-[9px] text-gray-600 uppercase tracking-wider font-semibold">
                              Full catalog — type to search
                            </span>
                          </td>
                        </tr>
                      );
                    }

                    const row = (
                      <tr
                        key={k}
                        className={cls(
                          "transition-colors",
                          isAdded
                            ? "bg-green-900/10 opacity-60"
                            : item._isBudget
                              ? "bg-teal-900/10 hover:bg-teal-900/20"
                              : "hover:bg-gray-800/50",
                        )}
                      >
                        {/* Code + source badge */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono font-bold text-[11px] text-orange-400">
                              <Highlight
                                text={item.itemCode || "—"}
                                tokens={queryToks}
                              />
                            </span>
                            {item._isBudget ? (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-teal-900/60 text-teal-300 border border-teal-800/60 w-fit">
                                ★ BUDGET
                              </span>
                            ) : (
                              <span className="text-[9px] font-medium px-1 py-0.5 rounded bg-gray-700/60 text-gray-500 border border-gray-700 w-fit">
                                CATALOG
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-3 py-2.5">
                          <p className="text-gray-200 text-[12px] font-medium leading-snug">
                            <Highlight
                              text={
                                item.description ||
                                item.costCodeDescription ||
                                item.itemDescription ||
                                "—"
                              }
                              tokens={queryToks}
                            />
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {item.defaultVendor?.companyName && (
                              <span className="text-[10px] text-gray-500">
                                {item.defaultVendor.companyName}
                              </span>
                            )}
                            {isCov && (
                              <span className="text-[9px] font-bold text-blue-400">
                                {uom.toUpperCase().includes("SF") ? "SF" : "LF"}{" "}
                                coverage
                              </span>
                            )}
                            {item.vendorPrices?.length > 1 && (
                              <span className="text-[9px] text-gray-600">
                                {item.vendorPrices.length} vendors
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Size / dimension input */}
                        <td className="px-3 py-2.5">
                          {isCov ? (
                            <div className="space-y-1">
                              <input
                                type="text"
                                value={rawSize}
                                onChange={(e) =>
                                  setSizes((p) => ({
                                    ...p,
                                    [k]: e.target.value,
                                  }))
                                }
                                placeholder={
                                  uom.toUpperCase().includes("SF")
                                    ? "4'×8'"
                                    : "8'"
                                }
                                onClick={(e) => e.stopPropagation()}
                                className={cls(
                                  "w-full px-2 py-1 text-[11px] border rounded font-mono focus:outline-none",
                                  rawSize
                                    ? covOk
                                      ? "bg-gray-900 border-green-700 text-green-300 focus:border-green-500"
                                      : "bg-gray-900 border-yellow-700 text-yellow-300 focus:border-yellow-500"
                                    : "bg-gray-800 border-gray-600 text-gray-300 focus:border-orange-500",
                                )}
                              />
                              {covOk && (
                                <p className="text-[9px] text-green-400 font-mono font-bold">
                                  = {coverage} {uom}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-[11px] font-mono">
                              <Highlight
                                text={item.size || "—"}
                                tokens={queryToks}
                              />
                            </span>
                          )}
                        </td>

                        {/* UOM */}
                        <td className="px-3 py-2.5 text-center">
                          <span
                            className={cls(
                              "text-[10px] font-semibold px-1.5 py-0.5 rounded",
                              isCov
                                ? "text-blue-300 bg-blue-900/30"
                                : "text-gray-400 bg-gray-700/50",
                            )}
                          >
                            {isCov ? "PC" : uom || "EA"}
                          </span>
                        </td>

                        {/* Section */}
                        <td className="px-3 py-2.5">
                          {item.sectionNumber ? (
                            <span className="text-[10px] font-mono text-blue-400">
                              <Highlight
                                text={item.sectionNumber}
                                tokens={queryToks}
                              />
                            </span>
                          ) : (
                            <span className="text-gray-700">—</span>
                          )}
                        </td>

                        {/* Qty + Add */}
                        <td className="px-2 py-2.5">
                          {isAdded ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-[10px] text-green-400 font-medium">
                                ✓ Added
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-gray-500">
                                  Qty:
                                </span>
                                <input
                                  type="number"
                                  value={qtys[k] ?? ""}
                                  min="0"
                                  placeholder="1"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    setQtys((p) => ({
                                      ...p,
                                      [k]: e.target.value,
                                    }))
                                  }
                                  className="w-14 px-1.5 py-0.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none"
                                />
                              </div>
                              <button
                                onClick={() => handleAdd(item)}
                                className={cls(
                                  "px-2.5 py-1 text-[10px] font-bold rounded-lg text-white transition-colors whitespace-nowrap",
                                  item._isBudget
                                    ? "bg-teal-600 hover:bg-teal-500"
                                    : "bg-orange-500 hover:bg-orange-600",
                                )}
                              >
                                + Add
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );

                    return divider ? [divider, row] : row;
                  });
                })()}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-3 border-t border-gray-700 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[10px] text-gray-600">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-teal-600/70 inline-block" />
              Budget items
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-gray-600/70 inline-block" />
              Catalog items
            </span>
            {isSearching && (
              <span className="text-gray-700 hidden sm:inline">
                Scores: stud/track depth+gauge+leg · compound types ·
                dimensional phrases · AI codes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {totalAdded > 0 && (
              <span className="text-xs text-teal-400 font-medium">
                {totalAdded} item{totalAdded !== 1 ? "s" : ""} added
              </span>
            )}
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors"
            >
              {totalAdded > 0 ? "Done" : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
